import { Link } from '@inertiajs/react'
import { ArrowRight, Check } from 'lucide-react'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import FeatureFaq from '@/components/marketing/features/FeatureFaq'
import BrandedPortalMockup from '@/components/marketing/technology/BrandedPortalMockup'
import TechCta from '@/components/marketing/technology/TechCta'
import TechnologyHero from '@/components/marketing/technology/TechnologyHero'
import TechSubnav from '@/components/marketing/technology/TechSubnav'
import type { TechnologyHubProps } from '@/types/technology'

/* The technology hub at /technology — a chooser, not the whole argument
 * (plan 2026-10-18, audience-first).
 *
 * The owner's problem was seventeen sections and ~4,500 words, "scrambled". So
 * the hub is SIX sections and nothing else: hero → who is it for (four
 * audience cards, each to the page that tells that buyer's story) → see it
 * running → capabilities at a glance (six groups, then the full list on
 * /technology/capabilities) → proof → FAQ and the closing CTA.
 *
 * The full module list and the roadmap live on /technology/capabilities. Do not
 * add them back here; that is how the hub grew to seventeen sections.
 *
 * The FAQ is never inside a ScrollReveal: its answers must be visible text
 * without scrolling or JavaScript.
 */
export default function TechnologyIndex({
  hero,
  answer,
  audiences,
  showcase,
  capabilities,
  proof,
  subnav,
  brand,
  faqs,
  cta,
}: TechnologyHubProps) {
  return (
    <>
      <TechnologyHero
        eyebrow={hero.eyebrow}
        title={hero.title}
        body={hero.body}
        action={cta.action}
        anchor={hero.anchor}
      />

      <TechSubnav items={subnav} brand={brand} />

      {/* ── Who is it for? ──────────────────────────────────────────────── */}
      <section
        id="who-is-it-for"
        aria-labelledby="who-is-it-for-title"
        className="container mx-auto scroll-mt-52 sm:scroll-mt-44 px-6 pt-20 lg:pt-24"
      >
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <h2 id="who-is-it-for-title" className="marketing-section-title">
              {audiences.title}
            </h2>
            <p className="marketing-lead mt-5 max-w-[65ch]">{answer}</p>
          </div>

          <ul className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {audiences.items.map((card) => (
              <li key={card.key}>
                <Link
                  href={card.href}
                  prefetch
                  data-audience-card={card.key}
                  className="group marketing-surface-card flex h-full flex-col p-6 no-underline"
                >
                  <h3
                    className="text-lg font-semibold text-white"
                    style={{ fontFamily: 'var(--wavbee-font-display)', letterSpacing: '-0.01em' }}
                  >
                    {card.title}
                  </h3>
                  <p className="mt-2 text-[0.9375rem] font-medium leading-relaxed text-[#ff8c4d]">
                    {card.line}
                  </p>
                  <ul className="mt-5 flex flex-1 flex-col gap-2.5">
                    {card.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-2.5 text-[0.875rem] leading-relaxed text-white/70">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#ff6a1a]" aria-hidden="true" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  {/* Where the card goes, said once: a quiet row, not a section
                      eyebrow — its tracking and side rules wrap badly in a card. */}
                  <p className="mt-6 flex items-center justify-between gap-3 border-t border-white/[0.08] pt-4 text-[0.875rem] font-medium text-white/70 transition-colors group-hover:text-white">
                    <span>{card.page_name}</span>
                    <ArrowRight
                      className="h-4 w-4 shrink-0 text-white/40 transition-[color,transform] group-hover:translate-x-0.5 group-hover:text-[#ff6a1a] motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
                      aria-hidden="true"
                    />
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── See it running ──────────────────────────────────────────────── */}
      <section
        id="see-it-running"
        aria-labelledby="see-it-running-title"
        className="container mx-auto scroll-mt-52 sm:scroll-mt-44 px-6 pt-20 lg:pt-24"
      >
        <div className="mx-auto max-w-5xl">
          <div className="mx-auto max-w-3xl text-center">
            <h2 id="see-it-running-title" className="marketing-section-title mx-auto">
              {showcase.title}
            </h2>
            <p className="marketing-lead mx-auto mt-5 max-w-[65ch]">{showcase.body}</p>
          </div>
          <ScrollReveal>
            <div data-hub-visual="" className="mt-12">
              <BrandedPortalMockup />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── Capabilities at a glance ────────────────────────────────────── */}
      <section
        id="capabilities"
        aria-labelledby="capabilities-title"
        className="container mx-auto scroll-mt-52 sm:scroll-mt-44 px-6 pt-20 lg:pt-24"
      >
        <div className="mx-auto max-w-6xl">
          <h2 id="capabilities-title" className="marketing-section-title">
            {capabilities.title}
          </h2>
          <ul className="mt-10 grid gap-x-8 gap-y-2 md:grid-cols-2 lg:grid-cols-3">
            {capabilities.groups.map((group) => (
              <li key={group.key} className="border-t border-white/[0.08]">
                <a
                  href={group.href}
                  data-capability-group={group.key}
                  className="group block py-5 no-underline"
                >
                  <h3 className="flex items-center gap-2 text-base font-semibold text-white transition-colors group-hover:text-[#ff8c4d]">
                    {group.title}
                    <ArrowRight className="h-4 w-4 shrink-0 text-white/40 group-hover:text-[#ff6a1a]" aria-hidden="true" />
                  </h3>
                  <p className="mt-1.5 text-[0.9375rem] leading-relaxed text-white/65">{group.line}</p>
                </a>
              </li>
            ))}
          </ul>
          <a
            href={capabilities.all.href}
            className="marketing-inline-link mt-8 inline-flex items-center gap-1.5 text-[0.9375rem] font-semibold hover:!text-[#ff6a1a]"
          >
            {capabilities.all.label}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>

      {/* ── Proof ───────────────────────────────────────────────────────── */}
      <section aria-labelledby="proof-title" className="container mx-auto px-6 py-20 lg:py-24">
        <div className="mx-auto max-w-6xl">
          <h2 id="proof-title" className="marketing-section-title">
            {proof.title}
          </h2>

          <dl className="mt-10 grid gap-4 sm:grid-cols-3">
            {proof.metrics.map((metric) => (
              <div
                key={metric.label}
                className="flex flex-col-reverse rounded-2xl border border-white/[0.08] bg-white/[0.03] px-5 py-6 text-center"
              >
                <dt className="mt-1.5 text-[0.875rem] leading-snug text-white/65">{metric.label}</dt>
                <dd
                  className="text-3xl font-bold text-[#ff6a1a]"
                  style={{ fontFamily: 'var(--wavbee-font-display)' }}
                >
                  {metric.value}
                </dd>
              </div>
            ))}
          </dl>

          <h3 className="mt-14 text-lg font-semibold text-white" style={{ fontFamily: 'var(--wavbee-font-display)' }}>
            {proof.deployment.title}
          </h3>
          <ol className="mt-5 grid gap-4 md:grid-cols-3">
            {proof.deployment.models.map((model) => (
              <li key={model.key} className="rounded-2xl border border-white/[0.08] p-5">
                <p className="marketing-eyebrow-mono">
                  {model.label}
                </p>
                <p className="mt-2 text-[0.9375rem] leading-relaxed text-white/75">{model.title}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── FAQ, then the one action again ─────────────────────────────── */}
      <FeatureFaq faq={faqs} heading="Questions" />

      <TechCta cta={cta} />
    </>
  )
}
